import React from 'react';
import { ApolloQueryResult, FetchResult } from '@apollo/client';

import LoadingSpinner, {
  TYPES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';
import Comments from '@buyside/components/presentation/Feed/Comments/comments';
import CommentInput from '@buyside/components/presentation/Feed/Comments/CommentInput/commentInput';

const CommentsQuery = require('buyside/graphql/queries/Comments');

import {
  Maybe,
  EventObject,
  CreateCommentMutation as CreateCommentMutationData,
  CreateCommentMutationVariables,
  CommentsQuery as CommentsQueryData,
  CommentsQueryVariables,
  useCommentsQuery,
  useCreateCommentMutation
} from '@interfaces/graphql-typings';

const CommentsContainer: React.FC<{
  allowAnonymousComment: boolean;
  commentsCount: number;
  currentUserId: string;
  eventObject?: EventObject;
  feedItemId: string;
  isAllocator: boolean;
  isRetail: boolean;
  profileImageUrl: string;
}> = ({
  allowAnonymousComment,
  commentsCount,
  currentUserId,
  eventObject,
  feedItemId,
  isAllocator,
  isRetail,
  profileImageUrl
}): React.ReactElement => {
  const { data, loading, fetchMore } = useCommentsQuery({
    variables: { feedItemId }
  });

  const [createComment] = useCreateCommentMutation({
    update(cache, { data }) {
      const newComment = data?.createComment?.comment;
      const existingCommentData = cache.readQuery<CommentsQueryData, CommentsQueryVariables>({
        query: CommentsQuery,
        variables: { feedItemId }
      });
      if (existingCommentData && newComment) {
        cache.writeQuery({
          query: CommentsQuery,
          data: {
            feedItemComments: {
              edges: [
                { node: newComment },
                ...(existingCommentData?.feedItemComments?.edges || [])
              ],
              pageInfo: existingCommentData?.feedItemComments?.pageInfo
            },
            feedItemShow: existingCommentData.feedItemShow
          }
        });
      }

      cache.modify({
        id: cache.identify({ id: eventObject?.id, __typename: eventObject?.__typename }),
        fields: {
          commentsCount: (cachedCount): number => cachedCount + 1
        }
      });
    }
  });

  if (loading) return <LoadingSpinner type={TYPES.SECONDARY} />;

  return (
    <>
      {!isRetail ? (
        <CommentInput
          allowAnonymousComment={allowAnonymousComment}
          handleComment={({
            body,
            anonymous
          }: CreateCommentMutationVariables): Promise<FetchResult<CreateCommentMutationData>> => {
            // @ts-ignore
            if (window.hj) {
              // @ts-ignore
              window.hj('tagRecording', ['activity_feed_comment_action']);
            }
            return createComment({ variables: { feedItemId, body, anonymous } });
          }}
          mentionables={data?.feedItemShow?.feedItemMentionables || []}
          profileImageUrl={profileImageUrl}
          showAllocatorNotification={isAllocator}
        />
      ) : null}
      {data?.feedItemComments?.edges?.length ? (
        <Comments
          canCommentAnonymously={allowAnonymousComment}
          comments={data.feedItemComments}
          commentsCount={commentsCount}
          currentUserId={currentUserId}
          eventObject={eventObject}
          feedItemId={feedItemId}
          loadMoreEntries={(): Maybe<Promise<ApolloQueryResult<CommentsQueryData>>> => {
            if (data.feedItemComments?.pageInfo?.hasNextPage) {
              return fetchMore({
                variables: {
                  feedItemId: feedItemId,
                  cursor: data.feedItemComments.pageInfo.endCursor
                }
              });
            } else {
              return null;
            }
          }}
          mentionables={data?.feedItemShow?.feedItemMentionables}
          isAllocator={isAllocator}
          postId={eventObject?.__typename === 'Post' ? eventObject.id : ''}
          profileImageUrl={profileImageUrl}
        />
      ) : null}
    </>
  );
};

export default CommentsContainer;
